import type { Sim, CoordinationRequest } from '@atc/sim';

/** A working position panel: decisions and feedback come from the simulation. */
export function mountTowerOperations(root: HTMLElement, sim: Sim, select: (callsign: string) => void) {
  root.hidden = sim.operations().position !== 'tower';
  if (root.hidden) return () => {};
  root.innerHTML = `<h2>Tower position</h2><p class="tower-help">Accept inbound transfers, work the runway, then contact the next controller.</p>
    <label>Arrival flow <select aria-label="Arrival flow"><option value="normal">Normal</option><option value="spaced">Increase spacing</option><option value="suspended">Suspend new arrivals</option></select></label>
    <p class="tower-result" role="status"></p><div class="tower-arrivals" aria-label="Arrival timeline"></div>
    <div class="tower-transfers" aria-label="Control transfers"></div><details><summary>Coordination log</summary><ol class="tower-log"></ol></details>`;
  const output = root.querySelector<HTMLElement>('.tower-result')!;
  const list = root.querySelector<HTMLElement>('.tower-transfers')!;
  const arrivals = root.querySelector<HTMLElement>('.tower-arrivals')!;
  const log = root.querySelector<HTMLElement>('.tower-log')!;
  const flow = root.querySelector<HTMLSelectElement>('select')!;
  const send = (request: CoordinationRequest) => {
    if (sim.paused) return;
    const result = sim.coordinate(request);
    output.textContent = result.message;
    output.classList.toggle('error', !result.ok);
    render(true);
  };
  flow.addEventListener('change', () => send({ kind: 'arrivalFlow', mode: flow.value as 'normal' | 'spaced' | 'suspended' }));
  let previous = '', lastTime = -1;
  function render(force = false) {
    if (!force && Math.floor(sim.simTimeS) === lastTime) return;
    lastTime = Math.floor(sim.simTimeS);
    const state = sim.operations();
    flow.value = state.arrivalFlow;
    flow.disabled = sim.paused;
    const signature = JSON.stringify([state.aircraft, sim.paused]);
    if (force || signature !== previous) {
      previous = signature;
      // Keep focused controls stable while a controller is acting on them.
      if (!list.contains(document.activeElement) || force) {
        list.replaceChildren();
        for (const entry of state.aircraft) {
          const { control: c, callsign } = entry;
          if (c.owner !== 'tower' && c.offeredTo !== 'tower' && !entry.crossing) continue;
          const row = document.createElement('div'); row.className = 'tower-transfer';
          const name = document.createElement('button'); name.className = 'g-btn'; name.textContent = callsign; name.onclick = () => select(callsign);
          const owner = document.createElement('small'); owner.textContent = c.transferTo ? `Contacting ${c.transferTo}` : `${c.owner} → ${c.offeredTo ?? 'on frequency'}`;
          row.append(name, owner);
          if (entry.crossing) {
            const cross = document.createElement('button'); cross.className = 'g-btn'; cross.textContent = `Cross ${entry.crossing}`; cross.disabled = sim.paused;
            cross.onclick = () => send({ kind: 'crossRunway', callsign }); row.append(cross);
          }
          if (c.offeredTo) {
            const action = document.createElement('button'); action.className = 'g-btn'; action.disabled = sim.paused;
            action.textContent = c.offeredTo === 'tower' ? 'Accept' : `Contact ${c.offeredTo}`;
            action.onclick = () => send(c.offeredTo === 'tower' ? { kind: 'accept', callsign } : { kind: 'transfer', callsign, to: c.offeredTo! });
            row.append(action);
          }
          const ac = sim.find(callsign);
          if (c.owner === 'tower' && ac && ['holdShort', 'lineUp', 'linedUp'].includes(ac.phase)) {
            const hold = document.createElement('button'); hold.className = 'g-btn'; hold.textContent = 'Hold position'; hold.disabled = sim.paused;
            hold.onclick = () => send({ kind: 'holdPosition', callsign }); row.append(hold);
          }
          list.append(row);
        }
        if (!list.childElementCount) list.textContent = 'Waiting for approach or ground to offer traffic.';
      }
      arrivals.replaceChildren();
      const title = document.createElement('b'); title.textContent = 'Arrivals · estimated time to threshold'; arrivals.append(title);
      for (const a of state.aircraft.filter((a) => a.thresholdInS !== undefined).sort((a, b) => a.thresholdInS! - b.thresholdInS!).slice(0, 5)) {
        const row = document.createElement('div'); row.className = 'tower-eta';
        row.textContent = `${a.callsign}  ${Math.floor(a.thresholdInS! / 60)}:${String(a.thresholdInS! % 60).padStart(2, '0')}  ${a.control.owner}`;
        arrivals.append(row);
      }
    }
    log.replaceChildren(...state.messages.slice(-5).reverse().map((m) => {
      const li = document.createElement('li'); li.textContent = `${Math.floor(m.timeS / 60)}:${String(Math.floor(m.timeS % 60)).padStart(2, '0')} ${m.from}: ${m.text}`; return li;
    }));
  }
  render();
  const off = sim.on('tick', () => render());
  return () => { off(); root.replaceChildren(); };
}
